import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "wouter";
import { Activity, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { useAcceptPracticeInvite } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";

export default function JoinPractice() {
  const params = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const accept = useAcceptPracticeInvite();
  const acceptRef = useRef(accept);
  acceptRef.current = accept;
  const attemptedRef = useRef(false);
  const [state, setState] = useState<"loading" | "joined" | "error">("loading");
  const [practiceName, setPracticeName] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!params.token || attemptedRef.current) return;
    attemptedRef.current = true;
    acceptRef.current.mutateAsync({ token: params.token })
      .then((practice) => {
        setPracticeName(practice.name);
        setState("joined");
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : "Could not accept invite";
        setError(message);
        setState("error");
      });
  }, [params.token]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-8 max-w-md w-full text-center space-y-4">
        <div className="bg-teal-50 p-3 rounded-2xl inline-block">
          <Activity className="h-6 w-6 text-teal-700" />
        </div>

        {state === "loading" && (
          <>
            <h1 className="text-xl font-bold text-slate-900">Joining practice…</h1>
            <Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" />
          </>
        )}

        {state === "joined" && (
          <>
            <CheckCircle2 className="h-10 w-10 text-teal-600 mx-auto" />
            <h1 className="text-xl font-bold text-slate-900">You've joined {practiceName}</h1>
            <p className="text-sm text-slate-500">
              The practice is now part of your workspace. Switch to it from the practice menu in the top bar.
            </p>
            <Button
              onClick={() => {
                const role = localStorage.getItem("rheumhub_role") ?? "nurse";
                setLocation(`/${role}`);
              }}
              className="bg-teal-700 hover:bg-teal-800 text-white w-full"
            >
              Go to dashboard
            </Button>
          </>
        )}

        {state === "error" && (
          <>
            <AlertCircle className="h-10 w-10 text-red-500 mx-auto" />
            <h1 className="text-xl font-bold text-slate-900">Couldn't accept invite</h1>
            <p className="text-sm text-slate-500">{error || "This invite may have expired or already been used."}</p>
            <Button variant="outline" onClick={() => setLocation("/")} className="w-full">
              Back to home
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
